import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Briefcase, TrendingUp, DollarSign, FileText, Clock } from 'lucide-react';
import { formatCurrency } from '../../lib/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BrokerDashboard = () => {
  const { getAuthHeaders, user } = useAuth();
  const [stats, setStats] = useState({
    totalDeals: 0,
    activeDeals: 0,
    totalCommissions: 0,
    pendingApplications: 0,
    recentDeals: [],
  });
  const [loading, setLoading] = useState(true);
  const [commissionDateRange, setCommissionDateRange] = useState('all');

  useEffect(() => {
    const fetchStats = async () => {
      try {
        // Fetch deals for this broker (the endpoint already filters by broker_id for broker users)
        // Use a high limit to get all deals
        const dealsResponse = await axios.get(`${API}/deals`, { 
          headers: getAuthHeaders(),
          params: { limit: 10000 }
        }).catch(() => ({ data: [] }));
        const myDeals = dealsResponse.data || [];
        
        // Count active deals (status is not Paid Off, Defaulted, or Cancelled)
        const activeStatuses = ['Active', 'Funded', 'In Progress', 'Pending'];
        const activeDeals = myDeals.filter(deal => 
          activeStatuses.includes(deal.status) || 
          (deal.status && !['Paid Off', 'Defaulted', 'Cancelled', 'Closed'].includes(deal.status))
        ).length;
        
        // Fetch commission balance
        const commissionParams = commissionDateRange !== 'all' ? { range: commissionDateRange } : {};
        const commissionResponse = await axios.get(`${API}/commissions/my-balance`, { 
          headers: getAuthHeaders(), 
          params: commissionParams 
        }).catch(() => ({ data: { total_earned: 0 } }));
        
        // Fetch my applications count
        const applicationsResponse = await axios.get(`${API}/deal-applications/my-applications`, { 
          headers: getAuthHeaders() 
        }).catch(() => ({ data: [] }));
        const pendingApps = applicationsResponse.data?.filter(app => 
          app.status === 'Pending' || app.status === 'Under Review'
        ).length || 0;
        
        setStats({
          totalDeals: myDeals.length,
          activeDeals: activeDeals,
          totalCommissions: commissionResponse.data?.total_earned || 0,
          pendingApplications: pendingApps,
        });
      } catch (error) {
        console.error('Failed to fetch broker stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [getAuthHeaders, commissionDateRange]);

  const statCards = [
    {
      title: 'My Deals',
      value: stats.totalDeals,
      icon: Briefcase,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      description: 'Deals you\'re involved in',
    },
    {
      title: 'Active Deals',
      value: stats.activeDeals,
      icon: TrendingUp,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
    },
    {
      title: 'Total Commissions',
      value: formatCurrency(stats.totalCommissions),
      icon: DollarSign,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
      description: 'Your earned commissions',
    },
    {
      title: 'Pending Applications',
      value: stats.pendingApplications,
      icon: Clock,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      description: 'Applications awaiting review',
    },
  ];

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header with filters */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-800">Broker Overview</h2>
          <p className="text-sm text-gray-500">Welcome back, {user?.name}</p>
        </div>
        <div className="mt-4 md:mt-0 flex items-center space-x-2">
          <span className="text-sm text-gray-600">Commission Period</span>
          <Select value={commissionDateRange} onValueChange={setCommissionDateRange}>
            <SelectTrigger className="w-32 h-9">
              <SelectValue placeholder="Range" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All time</SelectItem>
              <SelectItem value="month">Last 30 days</SelectItem>
              <SelectItem value="week">Last 7 days</SelectItem>
              <SelectItem value="day">Today</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  {stat.title}
                </CardTitle>
                <div className={`p-2 rounded-lg ${stat.bgColor}`}>
                  <Icon className={`h-4 w-4 ${stat.color}`} />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stat.value}</div>
                {stat.description && (
                  <p className="text-xs text-gray-500 mt-1">{stat.description}</p>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <a href="/deals" className="p-4 border rounded-lg hover:bg-slate-50 transition-colors flex items-center gap-3">
              <Briefcase className="h-5 w-5 text-blue-600" />
              <div>
                <p className="font-medium">View Deals</p>
                <p className="text-sm text-gray-500">Manage your deals</p>
              </div>
            </a>
            <a href="/submit-application" className="p-4 border rounded-lg hover:bg-slate-50 transition-colors flex items-center gap-3">
              <FileText className="h-5 w-5 text-green-600" />
              <div>
                <p className="font-medium">Submit Application</p>
                <p className="text-sm text-gray-500">New deal application</p>
              </div>
            </a>
            <a href="/commissions" className="p-4 border rounded-lg hover:bg-slate-50 transition-colors flex items-center gap-3">
              <DollarSign className="h-5 w-5 text-emerald-600" />
              <div>
                <p className="font-medium">Commissions</p>
                <p className="text-sm text-gray-500">View earnings</p>
              </div>
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BrokerDashboard;
